import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import "./clamped-text.css";

/**
 * Text clamped to one line (or a few) with an ellipsis, as Carbon and PatternFly do:
 * no fade and no button per item. Only when the text is actually cut does it
 * show the full text on hover and open on a click (or Enter / Space), so a
 * list where everything fits looks like a plain list.
 */
export function ClampedText({ text, lines = 1, suffix }: { text: string; lines?: number; suffix?: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [cut, setCut] = useState(false);
  const element = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    const node = element.current;
    if (node && !open) setCut(node.scrollHeight > node.clientHeight + 1);
  }, [text, lines, open]);
  const toggles = cut || open;
  return (
    <span
      ref={element}
      className={`clamped-text${open ? " is-open" : ""}${toggles ? " is-toggle" : ""}`}
      style={{ WebkitLineClamp: open ? "unset" : lines }}
      title={cut ? text : undefined}
      {...(toggles ? {
        role: "button",
        tabIndex: 0,
        "aria-expanded": open,
        onClick: () => setOpen(!open),
        onKeyDown: (event: React.KeyboardEvent) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          setOpen(!open);
        },
      } : {})}
    >
      {text}
      {suffix}
    </span>
  );
}
